import React, { useState, useEffect } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import {
  fetchEvaluationVariants,
  createEvaluationVariant,
  updateEvaluationVariant,
  deleteEvaluationVariant,
} from "../../Redux/ActionCreators/jobsAction";

const EvaluationFit = ({ setType, jobId: propJobId }) => {
  const { jobId: paramsJobId } = useParams();
  const jobId = propJobId || paramsJobId;
  const dispatch = useDispatch();

  const variants = useSelector((state) => state.jobs.evaluationVariants || []);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", label_instructions: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [viewVariant, setViewVariant] = useState(null);

  const currentUserName = localStorage.getItem("name") || "";

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const loadVariants = async () => {
    setLoading(true);
    await dispatch(fetchEvaluationVariants(jobId));
    setLoading(false);
  };

  useEffect(() => {
    if (jobId) loadVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setForm({ name: "", label_instructions: "" });
    setEditId(null);
    setShowForm(false);
  };

  const handleAddNewClick = () => {
    setForm({ name: "", label_instructions: "" });
    setEditId(null);
    setShowForm(true);
  };

  const handleEditClick = (variant) => {
    setForm({ name: variant.name || "", label_instructions: variant.label_instructions || "" });
    setEditId(variant.id);
    setShowForm(true);
  };

  const handleDuplicateClick = (variant) => {
    const baseNameMatch = (variant.name || "").match(/^(.*) - duplicate(?: \((\d+)\))?$/);
    const baseName = baseNameMatch ? baseNameMatch[1] : variant.name || "";

    const existingNames = variants.map((v) => v.name);
    let newName = `${baseName} - duplicate`;
    if (existingNames.includes(newName)) {
      let counter = 2;
      while (existingNames.includes(`${baseName} - duplicate (${counter})`)) {
        counter++;
      }
      newName = `${baseName} - duplicate (${counter})`;
    }

    setForm({ name: newName, label_instructions: variant.label_instructions || "" });
    setEditId(null);
    setShowForm(true);
  };

  const handleDelete = async (variantId) => {
    setDeletingId(variantId);
    const result = await dispatch(deleteEvaluationVariant(jobId, variantId));
    setDeletingId(null);
    if (result?.success) {
      await loadVariants();
      showSnackbar("Evaluation fit deleted successfully", "success");
    } else {
      showSnackbar(result?.message || "Failed to delete evaluation fit due to duplicate");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.label_instructions.trim()) {
      showSnackbar("Name and label instructions are mandatory!", "error");
      return;
    }

    setSaveLoading(true);
    let result;
    if (editId) {
      result = await dispatch(
        updateEvaluationVariant(jobId, editId, { label_instructions: form.label_instructions })
      );
    } else {
      result = await dispatch(
        createEvaluationVariant(jobId, {
          name: form.name,
          label_instructions: form.label_instructions,
          display_order: variants.length + 1,
        })
      );
    }
    setSaveLoading(false);

    if (result?.success) {
      await loadVariants();
      showSnackbar(editId ? "Evaluation fit updated successfully!" : "Evaluation fit added successfully!", "success");
      resetForm();
    } else if (editId) {
      showSnackbar(result?.message || "Failed to update evaluation fit");
    } else {
      showSnackbar("Evaluation variant with this name already exists");
    }
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => (showForm ? resetForm() : setType(""))}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Job Variants</h3>
          <p>{variants.length} variant{variants.length === 1 ? "" : "s"}</p>
        </div>
        {!showForm && (
          <button className="eval-fit-add-btn" onClick={handleAddNewClick}>
            <span className="eval-fit-add-btn-icon">+</span>
            Add new variant
          </button>
        )}
      </div>

      {!showForm && (
        <div className="job-settings-body">
          {loading ? (
            <div
              className="loading-overlay"
              style={{ position: "relative", marginTop: 16, alignItems: "start", height: "32px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="eval-fit-table-outer">
              <div className="eval-fit-table-scroll">
                <table className="eval-fit-table">
                  <thead>
                    <tr>
                      <th className="eval-fit-th-name">Name</th>
                      <th className="eval-fit-th-instructions">Instructions</th>
                      <th className="eval-fit-th-date">Created At</th>
                      <th className="eval-fit-th-creator">Created By</th>
                      <th className="eval-fit-th-actions">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.length > 0 ? (
                      variants.map((variant) => (
                        <tr key={variant.id}>
                          <td className="eval-fit-td-name" title={variant.name}>{variant.name}</td>
                          <td className="eval-fit-td-instructions">
                            <span className="eval-fit-instruction-text" title={variant.label_instructions}>
                              {variant.label_instructions}
                            </span>
                          </td>
                          <td className="eval-fit-td-date">{formatDate(variant.created_at)}</td>
                          <td className="eval-fit-td-creator">{variant.created_by_name || currentUserName || "—"}</td>
                          <td className="eval-fit-td-actions">
                            <div className="eval-fit-actions">
                              <button className="eval-fit-action-btn" onClick={() => setViewVariant(variant)} title="View">
                                <img src="/images/eye-black-icon.svg" alt="view" width={18} height={18} />
                              </button>
                              <button className="eval-fit-action-btn" onClick={() => handleEditClick(variant)} title="Edit">
                                <img src="/images/square-pen-black.svg" alt="edit" width={18} height={18} />
                              </button>
                              <button className="eval-fit-action-btn" onClick={() => handleDuplicateClick(variant)} title="Duplicate">
                                <img src="/images/duplicate-icon.svg" alt="duplicate" width={18} height={18} />
                              </button>
                              <button
                                className="eval-fit-action-btn"
                                onClick={() => handleDelete(variant.id)}
                                title="Delete"
                                disabled={deletingId === variant.id}
                              >
                                <img src="/images/trash-bin-black.svg" alt="delete" width={18} height={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="eval-fit-empty-cell">No variants found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="job-settings-form">
          <div className="job-settings-form-question">
            <label>Name</label>
            <textarea
              placeholder="Enter name"
              value={form.name}
              disabled={!!editId}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="job-settings-form-question">
            <label>Instructions</label>
            <textarea
              className="label-instructions-textarea"
              placeholder="Enter label instructions"
              value={form.label_instructions}
              rows={3}
              onChange={(e) => setForm({ ...form, label_instructions: e.target.value })}
            />
          </div>
          <div className="d-flex gap-4 align-items-center">
            <button className="save-btn" type="button" onClick={handleSave} disabled={saveLoading}>
              {saveLoading ? "Saving..." : "Save"}
            </button>
            <h6 className="cursor-pointer cancle-txt" onClick={resetForm}>Cancel</h6>
          </div>
        </div>
      )}

      {viewVariant && (
        <div className="eval-fit-view-modal-overlay" onClick={() => setViewVariant(null)}>
          <div className="eval-fit-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="eval-fit-view-modal-head">
              <h3>{viewVariant.name}</h3>
              <button className="eval-fit-view-modal-close" onClick={() => setViewVariant(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="eval-fit-view-modal-body">
              <label>Instructions</label>
              <p>{viewVariant.label_instructions}</p>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EvaluationFit;
